import cn from 'classnames';
import React from 'react';
import { generatePath } from 'react-router';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { getButtonClassName } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';
import { RoutableLink } from 'apps/sigma2-ui/src/components/ui/RoutableLink';

export type LinkButtonProps = React.HTMLProps<HTMLAnchorElement> & {
  variant: LegacyButtonVariant;
  'data-qa': string;
};

export const LinkButton = React.forwardRef<HTMLAnchorElement, LinkButtonProps>(
  (props, ref) => {
    const { variant, href, className: customClassName } = props;

    let routerPath: string | undefined = '';

    try {
      routerPath = href ? generatePath(href) : undefined;
    } catch (error) {
      uiAppLogger.error(error);
    }

    const language = useLanguage();

    const className = cn(
      getButtonClassName(variant, props.disabled),
      customClassName,
    );

    const conditionalProps = {
      title: props.disabled
        ? language.submitDisabledFillAllInputs()
        : undefined,
    };

    return routerPath &&
      !href?.startsWith('#') &&
      !href?.startsWith('http://') &&
      !href?.startsWith('https://') ? (
      <RoutableLink
        {...props}
        {...conditionalProps}
        to={routerPath}
        className={className}
        ref={ref}
        onClick={(event) => {
          if (props.disabled) event.preventDefault();
        }}
      />
    ) : (
      <a
        {...props}
        {...conditionalProps}
        ref={ref}
        href={href}
        onClick={(e) => {
          // Scroll to the anchor
          if (href?.startsWith('#')) {
            e.preventDefault();
            const id = href.slice(1);
            const element = document.getElementById(id);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
            }
          }
        }}
        className={className}
      />
    );
  },
);

LinkButton.displayName = 'LinkButton';
